import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Popover from "@radix-ui/react-popover";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell, CalendarDays, ChartNoAxesColumn, Clapperboard, Library, LogOut, Map, Menu, NotebookPen,
  Settings, ShieldCheck, Sun, User as UserIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";

import { useAdminUsers } from "@/features/admin/api";
import { useAuth } from "@/features/auth/AuthContext";
import { get, post } from "@/shared/api/client";
import type { Notification } from "@/shared/api/types";
import { timeAgo } from "@/shared/lib/format";
import { Wordmark } from "@/shared/ui/brand";
import { cn } from "@/shared/ui/primitives";

const PRIMARY = [
  { to: "/today", label: "Today", icon: Sun },
  { to: "/paths", label: "My Learning Paths", short: "Paths", icon: Map },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/discoveries", label: "Discoveries", icon: NotebookPen },
  { to: "/progress", label: "Progress", icon: ChartNoAxesColumn },
];
const SECONDARY = [
  { to: "/resources", label: "Resources", icon: Library },
  { to: "/content", label: "Content Planner", icon: Clapperboard },
  { to: "/settings", label: "Settings", icon: Settings },
];

const ADMIN = { to: "/admin", label: "Members", icon: ShieldCheck };

/** Settings-area links; administrators also get Members. */
function useSecondary() {
  const { user } = useAuth();
  return user?.role === "admin" ? [ADMIN, ...SECONDARY] : SECONDARY;
}

/** Access requests waiting for a decision (administrators only). */
function useWaitingCount(): number {
  const { user } = useAuth();
  const waiting = useAdminUsers("pending", "", user?.role === "admin");
  return waiting.data?.counts.pending ?? 0;
}

function NavItem({ to, label, icon: Icon, count = 0 }: { to: string; label: string; icon: typeof Sun; count?: number }) {
  return (
    <NavLink to={to} className={({ isActive }) => cn(
      "flex h-10 items-center gap-3 rounded-[10px] px-3 text-[14px] font-medium transition-colors",
      isActive ? "bg-accent-soft text-accent-text" : "text-ink-2 hover:bg-sunken hover:text-ink")}>
      <Icon className="size-[18px]" aria-hidden />
      {label}
      {count > 0 && (
        <span className="ml-auto rounded-full bg-warn-soft px-2 text-[12px] font-semibold text-warn"
          aria-label={`${count} waiting for approval`}>{count}</span>
      )}
    </NavLink>
  );
}

function IntegrationStatus() {
  const status = useQuery({
    queryKey: ["system-status"],
    queryFn: () => get<{ ai: string; search: string }>("/api/v1/system/status"),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  if (!status.data) return null;
  const degraded = [status.data.ai, status.data.search].some((s) => s !== "available");
  if (!degraded) return null;
  const parts = [];
  if (status.data.ai !== "available") parts.push(status.data.ai === "not_configured" ? "AI assistant not set up" : "AI assistant offline");
  if (status.data.search !== "available") parts.push(status.data.search === "not_configured" ? "resource search not set up" : "resource search offline");
  return (
    <p className="rounded-xl bg-warn-soft px-3 py-2 text-[12px] leading-snug text-warn" role="status">
      {parts.join(" · ")}. Everything you've saved keeps working.
    </p>
  );
}

function Notifications() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: () => get<{ unread: number; items: Notification[] }>("/api/v1/notifications"),
    refetchInterval: 60_000,
  });
  const markAll = useMutation({
    mutationFn: () => post("/api/v1/notifications/read"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const unread = query.data?.unread ?? 0;
  return (
    <Popover.Root onOpenChange={(open) => { if (!open && unread) markAll.mutate(); }}>
      <Popover.Trigger className="relative rounded-[10px] p-2 text-ink-2 hover:bg-sunken hover:text-ink"
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}>
        <Bell className="size-5" aria-hidden />
        {unread > 0 && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-accent" />}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={8}
          className="z-50 w-[min(360px,calc(100vw-32px))] rounded-2xl border border-line bg-surface p-2 shadow-pop">
          <p className="px-3 pt-2 pb-1 text-[13px] font-semibold text-ink">Notifications</p>
          {!query.data?.items.length ? (
            <p className="px-3 py-6 text-center text-[14px] text-ink-3">You're all caught up.</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {query.data.items.map((n) => (
                <li key={n.id}>
                  <button type="button" onClick={() => n.link && navigate(n.link)}
                    className="w-full rounded-xl px-3 py-2.5 text-left hover:bg-sunken">
                    <span className="flex items-start gap-2">
                      {!n.read_at && <span className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" aria-label="Unread" />}
                      <span>
                        <span className="block text-[14px] font-medium text-ink">{n.title}</span>
                        {n.body && <span className="block text-[13px] text-ink-2">{n.body}</span>}
                        <span className="block text-[12px] text-ink-3">{timeAgo(n.created_at)}</span>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function AccountMenu({ trigger }: { trigger: ReactNode }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const secondary = useSecondary();
  const item = "flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-[14px] text-ink outline-none data-[highlighted]:bg-sunken";
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8}
          className="z-50 min-w-56 rounded-2xl border border-line bg-surface p-1.5 shadow-pop">
          <div className="px-2.5 py-2">
            <p className="text-[14px] font-semibold text-ink">{user?.display_name}</p>
            <p className="text-[12px] text-ink-3">{user?.email}</p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          {secondary.map(({ to, label, icon: Icon }) => (
            <DropdownMenu.Item key={to} className={item} onSelect={() => navigate(to)}>
              <Icon className="size-4 text-ink-3" aria-hidden />{label}
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Item className={item} onSelect={() => navigate("/settings?tab=account")}>
            <UserIcon className="size-4 text-ink-3" aria-hidden />Account
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <DropdownMenu.Item className={item} onSelect={async () => { await signOut(); navigate("/signin"); }}>
            <LogOut className="size-4 text-ink-3" aria-hidden />Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function AppShell() {
  const { user } = useAuth();
  const secondary = useSecondary();
  const waiting = useWaitingCount();
  const initials = (user?.display_name ?? "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="min-h-dvh">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-4 focus:py-2 focus:shadow-pop">
        Skip to content
      </a>
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-[248px] flex-col border-r border-line bg-surface-2 px-4 py-5 lg:flex">
        <Link to="/today" className="mb-8 px-2" aria-label="Person Learn home"><Wordmark /></Link>
        <nav aria-label="Primary" className="space-y-0.5">
          {PRIMARY.map((n) => <NavItem key={n.to} {...n} />)}
        </nav>
        <div className="my-4 h-px bg-line" />
        <nav aria-label="More" className="space-y-0.5">
          {secondary.map((n) => <NavItem key={n.to} {...n} count={n.to === ADMIN.to ? waiting : 0} />)}
        </nav>
        <div className="mt-auto space-y-3">
          <IntegrationStatus />
          <div className="flex items-center justify-between gap-2">
            <AccountMenu trigger={
              <button type="button" className="flex min-w-0 items-center gap-2.5 rounded-[10px] p-1.5 pr-3 text-left hover:bg-sunken"
                aria-label="Account menu">
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-soft text-[12px] font-semibold text-accent-text">{initials}</span>
                <span className="truncate text-[14px] font-medium text-ink">{user?.display_name}</span>
              </button>
            } />
            <Notifications />
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-line bg-bg/90 px-4 backdrop-blur lg:hidden">
        <Link to="/today" aria-label="Person Learn home"><Wordmark compact /></Link>
        <div className="flex items-center gap-1">
          <Notifications />
          <AccountMenu trigger={
            <button type="button" className="rounded-[10px] p-2 text-ink-2 hover:bg-sunken" aria-label="More and account">
              <Menu className="size-5" aria-hidden />
            </button>
          } />
        </div>
      </header>

      <main id="main" className="px-4 pt-6 pb-28 sm:px-8 lg:ml-[248px] lg:px-12 lg:pt-10 lg:pb-16">
        <div className="mx-auto max-w-[1080px]">
          <div className="mb-4 lg:hidden"><IntegrationStatus /></div>
          <Outlet />
        </div>
      </main>

      {/* Mobile tab bar */}
      <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
        <ul className="grid grid-cols-5">
          {PRIMARY.map(({ to, label, short, icon: Icon }) => (
            <li key={to}>
              <NavLink to={to} className={({ isActive }) => cn("flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium",
                isActive ? "text-accent-text" : "text-ink-3")}>
                <Icon className="size-[22px]" aria-hidden />
                {short ?? label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
